import { BookOpen, CalendarDays, Clock, CheckCircle, XCircle, Sparkles } from "lucide-react";
import { Link } from "react-router";

const QuizHistoryTab = ({
    loading,
    sessions,
}) => {

    // Format date to be more readable
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    // Calculate score percentage
    const calculateScore = (session) => {
        if (!session.total_question_count) return 0
        return Math.round((session.correct_answer_count / session.total_question_count) * 100)
    }

    // Check if a session is completed
    const isCompleted = (session) => {
        return session.completed_at !== null
    }

    // Get duration between start and completion
    const getQuizDuration = (session) => {
        if (!session.completed_at) return null

        const start = new Date(session.started_at)
        const end = new Date(session.completed_at)
        const diffMs = end - start
        const diffMins = Math.round(diffMs / 60000)

        return diffMins
    }

    return (
        <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
                <h2 className="card-title flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Quiz History
                </h2>
                <p className="text-sm opacity-70 -mt-1 mb-4">View your past quiz attempts and results</p>
                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="skeleton h-24 w-full rounded-lg"></div>
                        ))}
                    </div>
                ) : sessions.length === 0 ? (
                    <div className="text-center py-12 border rounded-lg bg-base-200">
                        <BookOpen className="h-12 w-12 mx-auto opacity-50 mb-4" />
                        <h3 className="text-lg font-medium mb-1">No quiz attempts yet</h3>
                        <p className="text-sm opacity-70">Start taking quizzes to build your history</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {sessions.map((session) => {
                            const score = calculateScore(session);
                            const completed = isCompleted(session);
                            const duration = getQuizDuration(session);

                            return (
                                <div key={session.id} className="card bg-base-200">
                                    <div className="card-body p-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-medium text-lg">{session?.topic_detail?.title || session?.course_title }</h3>
                                                    {completed ? (
                                                        <div className="badge badge-success gap-1">
                                                            <CheckCircle className="h-3 w-3" />
                                                            Completed
                                                        </div>
                                                    ) : (
                                                        <div className="badge badge-warning gap-1">
                                                            <XCircle className="h-3 w-3" />
                                                            Incomplete
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-xs opacity-70 mt-1">
                                                    {session?.topic_detail?.course_detail?.title || session?.course_title} (
                                                    {session?.topic_detail?.course_detail?.code || session?.course_title})
                                                </div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm opacity-70 mt-2">
                                                    <div className="flex items-center gap-1">
                                                        <CalendarDays className="h-3.5 w-3.5" />
                                                        <span>{formatDate(session.started_at)}</span>
                                                    </div>
                                                    {duration && (
                                                        <div className="flex items-center gap-1">
                                                            <Clock className="h-3.5 w-3.5" />
                                                            <span>
                                                                {duration} min{duration !== 1 ? "s" : ""}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                {completed && (
                                                    <div className="mt-3">
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span>
                                                                Score: {session.correct_answer_count}/{session.total_question_count}
                                                            </span>
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-medium">{score}%</span>
                                                                {score >= 70 && (
                                                                    <div className="badge badge-success gap-1">
                                                                        <Sparkles className="h-3 w-3" />
                                                                        Passed
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <progress
                                                            className={`progress w-full ${score >= 70 ? "progress-success" : score >= 50 ? "progress-warning" : "progress-error"}`}
                                                            value={score}
                                                            max="100"
                                                        ></progress>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex justify-end">
                                                <Link to={`/quizzes/${session.id}/results`} className="btn btn-primary btn-sm">
                                                    {completed ? "View Results" : "Continue"}
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>)
}

export default QuizHistoryTab;